import React, { useEffect, useState } from 'react';
import {
  Button,
  Chip,
  Divider,
  ListItemIcon,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import CheckIcon from '@mui/icons-material/Check';
import LogoutIcon from '@mui/icons-material/Logout';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { toast } from '@/lib/toast';

const SuperadminSwitcher = () => {
  const { user, isSuperadmin, isActingAs, actAs, exitActAs } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  const [tenants, setTenants] = useState([]);

  useEffect(() => {
    if (!isSuperadmin) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/tenants');
        if (!cancelled) setTenants(data || []);
      } catch (err) {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isSuperadmin]);

  if (!isSuperadmin) return null;

  const activeId = user?.tenant_id;
  const active = tenants.find((t) => t.id === activeId);

  const handleSelect = async (tenantId) => {
    setAnchorEl(null);
    if (tenantId === activeId) return;
    try {
      await actAs(tenantId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to switch tenant');
    }
  };

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        color="inherit"
        startIcon={<SwapHorizIcon />}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        data-testid="superadmin-switcher"
        sx={{ textTransform: 'none', maxWidth: 220 }}
      >
        <Typography variant="caption" noWrap sx={{ maxWidth: 140 }}>
          {active ? active.name : 'Select account'}
        </Typography>
        {isActingAs && <Chip label="acting" size="small" color="warning" sx={{ ml: 1, height: 18 }} />}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 240 } } }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 1, display: 'block' }}>
          Switch account
        </Typography>
        {tenants.map((t) => (
          <MenuItem key={t.id} selected={t.id === activeId} onClick={() => handleSelect(t.id)}>
            <ListItemIcon>{t.id === activeId ? <CheckIcon fontSize="small" /> : null}</ListItemIcon>
            {t.name}
          </MenuItem>
        ))}
        {isActingAs && (
          <>
            <Divider />
            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                exitActAs();
              }}
            >
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Return to superadmin
            </MenuItem>
          </>
        )}
      </Menu>
    </>
  );
};

export default SuperadminSwitcher;
